// Live tracking — the Attendance page's second tab: the manager's linked
// users (people at their structure node or below who share one of their
// projects) on the live-locations card. Mounting the tab fetches the
// snapshot and connects the /live-tracking socket (useLiveTracking); pins
// then move as fixes arrive and people drop offline when they clock out.
// "Pull latest position" (on the map) asks every linked user's app for a
// fresh fix right away. A pin card's "Show route history" opens the
// RouteHistoryDrawer for that person (the tab owns which one is open).
// The user's own node areas (GET users/own-nodes) draw under the pins as a
// backdrop; a failed areas fetch simply draws none.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Alert } from '@/components/common';
import { toLiveUsers, useGetOwnNodesQuery } from '@/api/endpoints/liveTrackingApi';
import { useLiveTracking } from '@/hooks/useLiveTracking';
import i18n from '@/i18n/config';
import LiveLocationsCard from '../LiveLocationsCard';
import RouteHistoryDrawer from './RouteHistoryDrawer';

const LiveTrackingTab = () => {
  const { t } = useTranslation();
  const { users, isLive, isError, lastUpdateAt, requestLocations } = useLiveTracking();
  const liveUsers = toLiveUsers(users);
  const { data: areas = [] } = useGetOwnNodesQuery();
  const [pulling, setPulling] = useState(false);
  // The person whose route drawer is open. Kept after close so the exit
  // animation doesn't lose its content; `routeOpen` drives visibility.
  const [routeUserId, setRouteUserId] = useState<string | null>(null);
  const [routeOpen, setRouteOpen] = useState(false);

  const pullLatest = async () => {
    setPulling(true);
    try {
      const requested = await requestLocations();
      if (requested) toast.success(t('live_tracking_request_sent', { count: requested }));
      else toast.error(t('live_tracking_request_none'));
    } catch (err) {
      toast.error((err as Error).message || t('error_generic'));
    } finally {
      setPulling(false);
    }
  };

  const asOfLabel = lastUpdateAt
    ? new Intl.DateTimeFormat(i18n.language, { hour: 'numeric', minute: '2-digit' }).format(
        new Date(lastUpdateAt),
      )
    : '—';

  const routeUser = routeUserId
    ? liveUsers.find((u) => u.companyUserId === routeUserId)
    : undefined;

  return (
    <div className='attendance-live-tab'>
      {isError && <Alert variant='danger'>{t('live_tracking_load_error')}</Alert>}

      <LiveLocationsCard
        className='attendance-live-tab__card'
        users={liveUsers}
        areas={areas}
        asOfLabel={asOfLabel}
        onPullLatest={() => void pullLatest()}
        pulling={pulling}
        // Only people on the clock can answer with a fix.
        canPull={isLive && liveUsers.some((u) => u.online)}
        onShowRoute={(id) => {
          setRouteUserId(id);
          setRouteOpen(true);
        }}
      />

      {routeUser && (
        <RouteHistoryDrawer
          open={routeOpen}
          onClose={() => setRouteOpen(false)}
          user={routeUser}
          liveUser={users.find((u) => u.companyUserId === routeUser.companyUserId)}
          isLive={isLive}
        />
      )}
    </div>
  );
};

export default LiveTrackingTab;
